import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';

const inviteSchema = z.object({
    email: z.string().email('Email inválido'),
    role: z.enum(['ADMIN', 'USER'], {
        errorMap: () => ({ message: 'Selecione um papel válido' })
    })
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface InviteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export function InviteModal({ isOpen, onClose, onSuccess }: InviteModalProps) {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<InviteFormData>({
        resolver: zodResolver(inviteSchema),
        defaultValues: {
            role: 'USER'
        }
    });

    const onSubmit = async (data: InviteFormData) => {
        try {
            const token = localStorage.getItem('auth_token');
            const tenantId = localStorage.getItem('selected_tenant_id');

            const response = await fetch('/api/invites', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...(tenantId && { 'X-Tenant-Id': tenantId })
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Erro ao enviar convite');
            }

            toast.success('Convite enviado com sucesso!');
            reset();
            onSuccess();
            onClose();
        } catch (error: unknown) {
            console.error('Erro ao enviar convite:', error);
            const errorMessage = error instanceof Error ? error.message : 'Falha ao enviar convite';
            toast.error(errorMessage);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-3xl bg-[#0f172a]/95 backdrop-blur-xl border border-white/10 p-0 text-left align-middle shadow-2xl transition-all relative z-10 animate-scaleIn">
                                {/* Efeito de Gradient de Fundo */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/10 rounded-full blur-3xl -mr-16 -mt-16" />
                                <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-600/10 rounded-full blur-3xl -ml-16 -mb-16" />

                                <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-transparent" />
                                    <div className="relative flex justify-between items-center">
                                        <Dialog.Title
                                            as="h3"
                                            className="text-2xl font-bold font-oswald text-white tracking-tight uppercase flex items-center gap-3"
                                        >
                                            <span className="p-2 bg-blue-500/10 rounded-xl border border-blue-500/20 text-xl">✉️</span>
                                            Convidar Usuário
                                        </Dialog.Title>
                                        <button
                                            onClick={onClose}
                                            className="text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-full w-10 h-10 flex items-center justify-center border border-white/10 hover:rotate-90 duration-300"
                                        >
                                            <span className="text-xl">✕</span>
                                        </button>
                                    </div>
                                </div>

                                <form onSubmit={handleSubmit(onSubmit)} className="relative p-8 space-y-8">
                                    <div className="space-y-6">
                                        <div className="group">
                                            <label className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-blue-400 transition-colors">
                                                Endereço de Email
                                            </label>
                                            <div className="relative">
                                                <input
                                                    {...register('email')}
                                                    type="email"
                                                    placeholder="email@exemplo.com"
                                                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all font-medium text-white placeholder-gray-500"
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-500 group-focus-within:text-blue-500 transition-colors">
                                                    @
                                                </div>
                                            </div>
                                            {errors.email && (
                                                <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                                                    {errors.email.message}
                                                </p>
                                            )}
                                        </div>

                                        <div className="group">
                                            <label className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-blue-400 transition-colors">
                                                Nível de Acesso
                                            </label>
                                            <select
                                                {...register('role')}
                                                className="w-full px-5 py-4 bg-[#0f172a] border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all font-medium text-white appearance-none"
                                            >
                                                <option value="USER">Usuário Comum</option>
                                                <option value="ADMIN">Administrador</option>
                                            </select>
                                            {errors.role && (
                                                <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                                                    {errors.role.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex gap-4 pt-4 border-t border-white/10">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="flex-1 px-6 py-4 text-gray-400 bg-white/5 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-white/10 hover:text-white transition-all border border-white/10"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 hover:shadow-xl hover:-translate-y-1 active:scale-95 disabled:opacity-50 relative overflow-hidden group/btn"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                            {isSubmitting ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                                                    <span>Enviando...</span>
                                                </div>
                                            ) : (
                                                'Enviar Convite'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
